/**
 * CorporateAuthService - Corporate authentication service
 * Handles corporate login, SSO integration, and corporate user management
 */

import apiClient from '../../services/api';
import TokenManager from './TokenManager';

export interface Corporate {
  id: number;
  company_name: string;
  industry?: string;
  headquarters_city?: string;
  employee_count?: number;
  contact_person_name?: string;
  contact_person_email?: string;
  contact_person_phone?: string;
  status: 'active' | 'pending' | 'inactive';
  created_at?: string;
}

export interface CorporateAuthResponse {
  success: boolean;
  token: string;
  refreshToken: string;
  expiresIn: number;
  corporate: Corporate;
  role: 'admin' | 'hr_manager' | 'finance' | 'viewer';
  message?: string;
}

export interface CorporateLoginCredentials {
  email: string;
  password: string;
}

export interface SSOCredentials {
  provider: 'google' | 'microsoft';
  idToken: string;
}

class CorporateAuthService {
  /**
   * Corporate login with email and password
   */
  async loginCorporate(credentials: CorporateLoginCredentials): Promise<CorporateAuthResponse> {
    try {
      const response = await apiClient.post<CorporateAuthResponse>(
        '/corporate/login',
        credentials
      );
      
      if (response.token && response.refreshToken) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn || 900,
          tokenType: 'Bearer'
        });
        
        // Store corporate data
        if (response.corporate) {
          localStorage.setItem('corporate', JSON.stringify(response.corporate));
          localStorage.setItem('corporateRole', response.role);
        }
      }
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Corporate login failed');
    }
  }

  /**
   * Corporate SSO login (Google)
   */
  async loginWithGoogle(idToken: string): Promise<CorporateAuthResponse> {
    try {
      const response = await apiClient.post<CorporateAuthResponse>(
        '/corporate/sso/google',
        { idToken }
      );
      
      if (response.token && response.refreshToken) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn || 900,
          tokenType: 'Bearer'
        });
        
        if (response.corporate) {
          localStorage.setItem('corporate', JSON.stringify(response.corporate));
          localStorage.setItem('corporateRole', response.role);
        }
      }
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Google SSO login failed');
    }
  }

  /**
   * Corporate SSO login (Microsoft)
   */
  async loginWithMicrosoft(idToken: string): Promise<CorporateAuthResponse> {
    try {
      const response = await apiClient.post<CorporateAuthResponse>(
        '/corporate/sso/microsoft',
        { idToken }
      );
      
      if (response.token && response.refreshToken) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn || 900,
          tokenType: 'Bearer'
        });
        
        if (response.corporate) {
          localStorage.setItem('corporate', JSON.stringify(response.corporate));
          localStorage.setItem('corporateRole', response.role);
        }
      }
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Microsoft SSO login failed');
    }
  }

  /**
   * Get corporate profile
   */
  async getCorporateProfile(): Promise<Corporate> {
    try {
      const response = await apiClient.get<Corporate>('/corporate/profile');
      
      // Update stored corporate data
      localStorage.setItem('corporate', JSON.stringify(response));
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch corporate profile');
    }
  }

  /**
   * Update corporate profile
   */
  async updateCorporateProfile(data: Partial<Corporate>): Promise<{ message: string; corporate: Corporate }> {
    try {
      const response = await apiClient.put<{ message: string; corporate: Corporate }>(
        '/corporate/profile',
        data
      );
      
      // Update stored corporate data
      localStorage.setItem('corporate', JSON.stringify(response.corporate));
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update corporate profile');
    }
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<{ token: string; expiresIn: number }> {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.post<{ token: string; expiresIn: number }>(
        '/corporate/refresh-token',
        { refreshToken }
      );
      
      if (response.token) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: refreshToken,
          expiresIn: response.expiresIn || 900,
          tokenType: 'Bearer'
        });
      }
      
      return response;
    } catch (error: any) {
      TokenManager.clearAllTokens();
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  }

  /**
   * Logout corporate user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/corporate/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      TokenManager.clearAllTokens();
      localStorage.removeItem('corporate');
      localStorage.removeItem('corporateRole');
    }
  }

  /**
   * Get stored corporate data
   */
  getStoredCorporate(): Corporate | null {
    try {
      const corporate = localStorage.getItem('corporate');
      return corporate ? JSON.parse(corporate) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get corporate user role
   */
  getCorporateRole(): string | null {
    return localStorage.getItem('corporateRole');
  }

  /**
   * Check if corporate user is authenticated
   */
  isAuthenticated(): boolean {
    return TokenManager.hasValidToken();
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const userRole = this.getCorporateRole();
    return userRole === role;
  }

  /**
   * Check if user has admin access
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Check if user is HR manager
   */
  isHRManager(): boolean {
    return this.hasRole('hr_manager');
  }

  /**
   * Check if user is finance
   */
  isFinance(): boolean {
    return this.hasRole('finance');
  }
}

const corporateAuthService = new CorporateAuthService();
export default corporateAuthService;
